import { Helmet } from 'react-helmet-async';
import { useI18n } from '../contexts/I18nContext';

const SITE_NAME = 'GEMS @ The Arts';
const SITE_URL = 'https://gemsatthearts.org';
const DEFAULT_IMAGE = `${SITE_URL}/og-image.jpg`;
const LOCALE_MAP = { en: 'en_US', es: 'es_US', pt: 'pt_BR' };

/**
 * SEO meta tag manager. Sets document title, meta description,
 * canonical URL, Open Graph/Twitter cards, hreflang alternates,
 * and any per-page JSON-LD structured data.
 */
export default function SEO({
  title,
  description,
  path = '/',
  image = DEFAULT_IMAGE,
  type = 'website',
  noindex = false,
  jsonLd = null,
}) {
  const { lang } = useI18n();
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — Arts. Enrichment. Community.`;
  const url = `${SITE_URL}${path}`;
  const imageAbs = image?.startsWith('http') ? image : `${SITE_URL}${image}`;
  const ogLocale = LOCALE_MAP[lang] || 'en_US';

  return (
    <Helmet>
      <html lang={lang} />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* hreflang alternates — same URL, just language-switched via UI */}
      <link rel="alternate" hrefLang="en" href={url} />
      <link rel="alternate" hrefLang="es" href={url} />
      <link rel="alternate" hrefLang="pt" href={url} />
      <link rel="alternate" hrefLang="x-default" href={url} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={imageAbs} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content={ogLocale} />
      {ogLocale !== 'en_US' && <meta property="og:locale:alternate" content="en_US" />}
      {ogLocale !== 'es_US' && <meta property="og:locale:alternate" content="es_US" />}
      {ogLocale !== 'pt_BR' && <meta property="og:locale:alternate" content="pt_BR" />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageAbs} />

      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
}
